import { Fragment, useCallback } from "react";
import { useFormikContext } from "formik";
// @ts-expect-error pure js
import TextArea from "../../UI/TextArea";
// @ts-expect-error pure js
import TextInput from "../../UI/TextInput";
// @ts-expect-error pure js
import ToggleSwitchFormik from "../../UI/ToggleSwitchFormik";
import CollapsibleCard from "../../UI/CollapsibleCard";
import Tooltip from "../../UI/Tooltip";
import Text from "../../UI/Text";
// @ts-expect-error pure js
import useClauses from "../../../hooks/use-clauses";
// @ts-expect-error pure js
import useAuth from "../../../hooks/use-auth.js";
import { TenantLeaseType } from "../../../utils/constants/tenants";
// @ts-expect-error pure js
import { ClauseType } from "../../../utils/constants/clauses";
import useDependentField, {
  usePropertyExtractor,
  // @ts-expect-error pure js
} from "../../../hooks/use-dependent-field";
// @ts-expect-error pure js
import { currencyFormatter } from "../../../api/Functions";
import type { LettingFees } from "../../../models/letting_fees";
import styles from "./ClausesForm.module.css";
import { IconInfoCircle } from "@tabler/icons-react";

interface Clause {
  static_id: string;
  title: string;
  subtitle: string;
  content: string;
}

interface ClausesFormProps {
  leaseType: string;
}

type LettingFeeField = keyof LettingFees;

type LettingFeesValues = Partial<Record<LettingFeeField, number | string>>;

interface ClausesFormValues {
  clauses_specifiques?: string[];
  letting_fees?: LettingFeesValues;
}

// Balises de la clause « honoraires » (bail-templates/clauses.json), rendues par le back
// à la génération du bail ; l'aperçu reflète la saisie en direct.
const LETTING_FEES_PLACEHOLDERS: Record<string, LettingFeeField> = {
  "[MontantBailleurHonoLoc]": "landlord_letting_fee",
  "[MontantTenantHonoLoc]": "tenant_letting_fee",
  "[MontantBailleurEDL]": "landlord_inventory_fee",
  "[MontantTenantHonoEDL]": "tenant_inventory_fee",
};
const LETTING_FEES_BLOCK_START = "[DebutMontantsHonoraires]";
const LETTING_FEES_BLOCK_END = "[FinMontantsHonoraires]";
const LETTING_FEES_BLOCK =
  /\[DebutMontantsHonoraires\][\s\S]*?\[FinMontantsHonoraires\]/g;

// Clauses réservées aux baux commerciaux et professionnels, par type de bail ; les autres
// types proposent toutes les clauses restantes.
const CLAUSES_PRO: Record<string, string[]> = {
  [TenantLeaseType.PROFESSIONNEL]: [ClauseType.SOUSLOCPRO],
  [TenantLeaseType.COMMERCIAL]: [ClauseType.SOUSLOCPRO],
  [TenantLeaseType.COMMERCIAL_DEROGATOIRE]: [
    ClauseType.SOUSLOCPRO,
    ClauseType.SANS_RENOUVELLEMENT,
  ],
};
const CLAUSES_PRO_UNIQUEMENT = new Set(Object.values(CLAUSES_PRO).flat());

function isClauseProposee(staticId: string, leaseType: string): boolean {
  const clausesPro = CLAUSES_PRO[leaseType];
  return clausesPro
    ? clausesPro.includes(staticId)
    : !CLAUSES_PRO_UNIQUEMENT.has(staticId);
}

/** Sans honoraires (bail d'un particulier), la clause ne mentionne aucun montant. */
function renderLettingFees(
  content: string,
  fees: LettingFeesValues | null,
): string {
  if (fees === null) {
    return content.replaceAll(LETTING_FEES_BLOCK, "");
  }
  return Object.entries(LETTING_FEES_PLACEHOLDERS).reduce(
    (rendered, [placeholder, field]) =>
      rendered.replaceAll(
        placeholder,
        currencyFormatter(Number(fees[field]) || 0),
      ),
    content
      .replaceAll(LETTING_FEES_BLOCK_START, "")
      .replaceAll(LETTING_FEES_BLOCK_END, ""),
  );
}

function LettingFeesFields() {
  return (
    <div className={styles.lettingFees}>
      <Text weight="bold">Honoraires de mise en location (TTC)</Text>
      <Text>
        La part du locataire ne peut pas dépasser celle du propriétaire ni les
        plafonds ci-dessus. Les montants saisis figurent dans la clause et
        génèrent les appels correspondants dans le compte du locataire et sur le
        bien.
      </Text>
      <div className={styles.lettingFeesGrid}>
        <TextInput
          type="number"
          name="letting_fees.landlord_letting_fee"
          label="Visite, dossier et bail — part propriétaire"
          unit="€"
          step="0.01"
          min="0"
          marginForm=""
        />
        <TextInput
          type="number"
          name="letting_fees.tenant_letting_fee"
          label="Visite, dossier et bail — part locataire"
          unit="€"
          step="0.01"
          min="0"
          marginForm=""
        />
        <TextInput
          type="number"
          name="letting_fees.landlord_inventory_fee"
          label="État des lieux d'entrée — part propriétaire"
          unit="€"
          step="0.01"
          min="0"
          marginForm=""
        />
        <TextInput
          type="number"
          name="letting_fees.tenant_inventory_fee"
          label="État des lieux d'entrée — part locataire"
          unit="€"
          step="0.01"
          min="0"
          marginForm=""
        />
      </div>
    </div>
  );
}

export default function ClausesForm({ leaseType }: ClausesFormProps) {
  return (
    <div>
      {![
        TenantLeaseType.STOCKAGE,
        TenantLeaseType.LCD,
        TenantLeaseType.SOUS_LOCATION,
      ].includes(leaseType) && <ClausesToggleSwitches leaseType={leaseType} />}
      <TextArea
        label={"Clause libre"}
        rows="4"
        name={"article_specific"}
        dependon="id_bien"
        marginForm={""}
      />
    </div>
  );
}

function ClausesToggleSwitches({ leaseType }: ClausesFormProps) {
  const { clauses } = useClauses() as { clauses: Clause[] };
  const { values } = useFormikContext<ClausesFormValues>();
  const { isPro } = useAuth() as { isPro: boolean };
  const fieldName = "clauses_specifiques";
  const extractClauses = usePropertyExtractor(fieldName) as (
    idBien: string,
  ) => Promise<string[] | undefined | null>;
  // Une agence facture ses honoraires de mise en location : la clause reste proposée cochée
  // même quand les clauses mémorisées sur le bien ne la contiennent pas.
  const extractClausesAvecHonoraires = useCallback(
    async (idBien: string) => {
      const clauses = await extractClauses(idBien);
      if (!isPro || !clauses || clauses.includes(ClauseType.HONORAIRE)) {
        return clauses;
      }
      return [...clauses, ClauseType.HONORAIRE];
    },
    [extractClauses, isPro],
  );
  useDependentField(fieldName, "id_bien", extractClausesAvecHonoraires);
  // Seule une agence facture des honoraires de mise en location : la saisie des montants
  // n'est proposée qu'aux comptes pro
  const saisieHonoraires =
    isPro && (values.clauses_specifiques ?? []).includes(ClauseType.HONORAIRE);
  return (
    <div className={styles.clausesList}>
      <Text weight="bold">
        Personnalisez votre contrat de location avec les clauses que vous
        souhaitez ajouter.
      </Text>
      {clauses
        .filter((c) => isClauseProposee(c.static_id, leaseType))
        .map(
          (clause, i) =>
            (![
              TenantLeaseType.GARAGE,
              TenantLeaseType.PARKING,
              TenantLeaseType.STOCKAGE,
            ].includes(leaseType) ||
              clause.static_id === ClauseType.SOUSLOC) && (
              <Fragment key={i}>
                <CollapsibleCard
                  title={
                    <>
                      {clause.title}
                      {clause.subtitle && (
                        <Tooltip content={clause.subtitle}>
                          <IconInfoCircle size={18} />
                        </Tooltip>
                      )}
                    </>
                  }
                  headerActions={
                    <ToggleSwitchFormik
                      name={fieldName}
                      value={clause.static_id}
                    />
                  }
                >
                  <Text
                    as="div"
                    dangerouslySetInnerHTML={{
                      __html:
                        clause.static_id === ClauseType.HONORAIRE
                          ? renderLettingFees(
                              clause.content,
                              isPro ? values.letting_fees ?? {} : null,
                            )
                          : clause.content,
                    }}
                    className={styles.clauseContent}
                  />
                </CollapsibleCard>
                {clause.static_id === ClauseType.HONORAIRE &&
                  saisieHonoraires && <LettingFeesFields />}
              </Fragment>
            ),
        )}
    </div>
  );
}
